import { Directive } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatStepper } from "@angular/material/stepper";
import { ActivatedRoute, Router } from "@angular/router";
import moment from "moment";
import { debounceTime, distinctUntilChanged, filter as rxFilter, map as rxMap, takeUntil } from "rxjs/operators";
import {
	IObservationRating,
	IPreviewDisabledObservation,
	OfflineRatingService,
} from "src/app/cbta-web/services/offline-rating-calculation.service";
import { CONSTANT } from "src/app/constant";
import { isTs1V3OrV4RatingFormVersion, isTs1V4RatingFormVersion } from "../utils/rating-form-version.utils";
import { CandidateRole, RouteType } from "src/app/models/enum";
import { CandidateRating, DraftItem, DraftItemForm, SignOffDetails } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import {
	confirmCompleteAssessment,
	confirmSendToCandidate,
} from "src/app/shared/utils/confirmation-dialog.utils";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { SESSION } from "src/session.config";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";
import { ComponentBase } from "../../shared/base/component-base";
import { draftSavePayload } from "../utils/draft-upsert.utils";
import { resolveFormDetailsForRating } from "../utils/form-config-version.utils";
import { resolveFlightSimAutocompleteOption } from "../flight-simulator-details.payload";
import { validateDraftCandidateStaffNumbers } from "../utils/draft-staff-validation.utils";
import {
	buildLastObResetTimeMap,
	filterAddRemoveObForRating,
	filterSelectedOBsForSummary,
	obKey,
} from "../utils/observation.utils";
import {
	buildTs1V3FullyDisabledObKeys,
	buildTs1V3NaRoleObKeys,
	includeTs1V3ObInRatingNewObs,
	isTs1V3ConfigObsExcludedByNa,
	isTs1V3ManeuverRoleOutcomeNa,
	mergeTs1V3ObEventsForRatingCalc,
	applyManeuverOutcomeNaDisabledToEvents,
} from "../../ts1-rating-form-v3/utils/ts1-rating-ab-ob-merge.utils";
import {
	buildTs1V3ManeuverCodesToExclude,
	shouldExcludeDisabledObForTs1V3,
} from "../../ts1-rating-form-v3/utils/ts1-rating-calc-preview.utils";
import { resolveOriginalObsCodesFromAssessmentConfig } from "../../ts1-rating-form-v4/calculation/original-obs-codes.utils";
import * as uuid from "uuid";

/**
 * Base class for multi-step rating forms.
 *
 * Responsibilities:
 * - Manage step navigation, draft persistence, and autosave bindings.
 * - Provide shared helper methods for candidate navigation and layout.
 * - Expose abstract hooks for child components to implement domain logic.
 */
@Directive()
export abstract class TsRatingFormV2Base extends ComponentBase {
	// Services expected in the subclass.
	protected abstract fb: UntypedFormBuilder;
	protected abstract route: ActivatedRoute;
	protected abstract router: Router;
	protected abstract loadingService: LoaderService;
	protected abstract sharedService: SharedService;
	protected abstract roleService: RoleService;
	protected abstract profileService: ProfileService;
	protected abstract dialog: MatDialog;
	protected abstract containerRatingsService: ContainerRatingsService;
	protected abstract httpService: HttpService;
	protected abstract stepper: MatStepper;
	protected abstract offlineRatingService: OfflineRatingService;

	// Top-level reactive forms provided by the subclass.
	protected abstract get trainingInfoForm(): UntypedFormGroup; // step 1
	protected abstract get ratingRootForm(): UntypedFormGroup; // step 2
	protected abstract get signOffRootForm(): UntypedFormGroup; // step 3

	// Shared constants.
	readonly MAX_CANDIDATES = 4;
	readonly MAX_CANDIDATES_TS1 = 2;
	readonly DATE_FORMAT = CONSTANT.DATE_FORMAT;

	// UI state.
	panelOpenState = false;
	currentDate: Date = new Date();
	disableForm = false;
	signOffSuccess = false;

	// Step flags.
	isStep1FormCreated = false;
	isStep2FormCreated = false;
	ratingFormLoaded = false;

	// Candidate navigation indices.
	current = 0;
	prev = -1;

	// Last-reached page index (1..3).
	continuePage = 1;

	// Common collections used by child components.
	selectedSkills: Array<string> = [];
	candidateList: Array<string> = [];
	completedCandidateList: Array<string> = [];
	sessionConfigs: any = [];
	sessionKeys: any = [];
	sessionFormFields: any[] = [];
	formSessionDetails: Array<string> = [];
	assessmentConfig: any[] = [];

	// Tracking for form metadata and drafts.
	formId: string = "";
	observationData: any;
	rawFormData?: any;

	draftId?: string;
	assessmentOfflineId?: string;

	// Internal flags.
	protected listenersAttached = false;
	protected isBootstrapping = false;

	// Common control accessors.
	protected get candidates(): UntypedFormArray {
		return this.trainingInfoForm.controls["candidates"] as UntypedFormArray;
	}
	protected get ratingForm(): UntypedFormArray {
		return this.ratingRootForm.controls["ratingForm"] as UntypedFormArray;
	}
	protected get signOffForm(): UntypedFormArray {
		return this.signOffRootForm.controls["signOffForm"] as UntypedFormArray;
	}
	protected get formTypeCtrl() {
		return this.trainingInfoForm.get("formType")!;
	}
	protected get formNameCtrl() {
		return this.trainingInfoForm.get("formName")!;
	}
	protected get syllabusStartDateCtrl() {
		return this.trainingInfoForm.get("syllabusStartDate")!;
	}
	protected get syllabusEndDateCtrl() {
		return this.trainingInfoForm.get("syllabusEndDate")!;
	}

	/**
	 * Resolve form and draft identity from the route. If a draft is referenced,
	 * preload it into the container service; otherwise, clear any prior draft.
	 */
	protected initializeComponent(): void {
		this.formId = this.getParamDeep("id") ?? "";
		this.draftId = this.getParamDeep("draftId") ?? undefined;

		if (this.draftId) {
			this.containerRatingsService.inDraftMode = true;
			this.assessmentOfflineId = this.draftId;
			this.containerRatingsService.loadDraftItem(this.draftId);
		} else {
			this.containerRatingsService.inDraftMode = false;
			this.assessmentOfflineId = undefined;
			this.containerRatingsService.clearDraft();
		}
	}

	/**
	 * Convert nested boolean map into grouped selection arrays.
	 */
	buildSessionSelection(raw: any): Record<string, string[]> {
		const out: any = {};
		Object.entries(raw ?? {}).forEach(([groupKey, groupValue]) => {
			if (!groupValue || typeof groupValue !== "object") return;
			const normalized = groupKey;
			const selected = Object.entries(groupValue as Record<string, boolean>)
				.filter(([, v]) => v === true)
				.map(([k]) => k);
			out[normalized] = selected;
		});
		// out.assessmentType = out.assessmentType ?? [];
		// out.trainingSession = out.trainingSession ?? [];
		return out;
	}

	/**
	 * Convert nested session remark map into draft/API payload shape.
	 */
	buildSessionRemarks(raw: any): Record<string, Record<string, string>> {
		const out: Record<string, Record<string, string>> = {};
		Object.entries(raw ?? {}).forEach(([groupKey, groupValue]) => {
			if (!groupValue || typeof groupValue !== "object") return;
			const remarks: Record<string, string> = {};
			Object.entries(groupValue as Record<string, unknown>).forEach(([code, val]) => {
				remarks[code] = (val ?? "").toString();
			});
			out[groupKey] = remarks;
		});
		return out;
	}

	/**
	 * Restore session remarks from draft candidate into nested form structure.
	 */
	toNestedSessionRemarks(cand: any): Record<string, Record<string, string>> {
		const stored = cand?.sessionRemarks?.[0] ?? cand?.sessionRemarks ?? {};
		const nested: Record<string, Record<string, string>> = {};

		for (const section of this.sessionKeys ?? []) {
			const sectionCode = section?.name?.code;
			if (!sectionCode) continue;
			nested[sectionCode] = {};
			for (const d of section?.details ?? []) {
				const code = typeof d === "string" ? d : d?.code;
				if (!code) continue;
				nested[sectionCode][code] = (stored[sectionCode]?.[code] ?? "").toString();
			}
		}

		for (const [groupKey, groupVal] of Object.entries(stored as Record<string, Record<string, string>>)) {
			if (!groupVal || typeof groupVal !== "object") continue;
			nested[groupKey] = nested[groupKey] ?? {};
			for (const [code, remark] of Object.entries(groupVal)) {
				nested[groupKey][code] = (remark ?? "").toString();
			}
		}

		return nested;
	}

	/**
	 * Get a route parameter from the current route or any parent segment.
	 */
	protected getParamDeep(key: string): string | undefined {
		let r: ActivatedRoute | null = this.route;
		while (r) {
			const v = r.snapshot.paramMap.get(key);
			if (v) return v;
			r = r.parent!;
		}
		return undefined;
	}

	/**
	 * Template calls this when the date control changes.
	 * Delegates to the base helper that patches the form.
	 */
	onStartDateChange(dateString: string) {
		this.saveSyllabusStartDate(dateString);
	}

	/**
	 * Resolve an observation control by category key and numeric index.
	 */
	getObControl(obsGroup: UntypedFormGroup, cat: string, num: number): UntypedFormControl | null {
		const catGroup = obsGroup.get(cat) as UntypedFormGroup | null;
		if (!catGroup) return null;

		const key1 = obKey(cat, num, true);
		const key2 = obKey(cat, num, false);

		const ctrl1 = catGroup.get(key1) as UntypedFormControl;
		const ctrl2 = catGroup.get(key2) as UntypedFormControl;

		return ctrl1 || ctrl2 || null;
	}

	/**
	 * Wait until SharedService has populated dropdown option lists.
	 * Required before patching select controls from a draft.
	 */
	protected async waitForOptions(): Promise<void> {
		return new Promise((resolve) => {
			const ready = () =>
				Array.isArray(this.sharedService.dropDownOptionsRank) &&
				this.sharedService.dropDownOptionsRank.length > 0 &&
				Array.isArray(this.sharedService.dropDownOptionsPilotProfile) &&
				this.sharedService.dropDownOptionsPilotProfile.length > 0;

			if (ready()) return resolve();
			const id = setInterval(() => {
				if (ready()) {
					clearInterval(id);
					resolve();
				}
			}, 50);
		});
	}

	/**
	 * Convert a primitive code to an option object from a list.
	 * Falls back to a {code, description} object so the control stays consistent.
	 */
	protected asOption(list: any[] | undefined, code?: string) {
		if (!code) return "";
		const found = list?.find((v: any) => v.code === code);
		return found ?? { code, description: code };
	}

	/** After draft patch, ensure acType is an autocomplete option object (draft stores a string). */
	protected rehydrateFlightSimulatorAcTypeFields(): void {
		const candidates = (this as unknown as { candidates?: UntypedFormArray }).candidates;
		if (!candidates) return;

		const options = this.sharedService.dropDownOptionsAircraftType;
		for (const control of candidates.controls) {
			const acType = (control as UntypedFormGroup).get("flightSimulatorDetails.acType");
			if (!acType) continue;

			const normalized = resolveFlightSimAutocompleteOption(acType.value, options);
			if (JSON.stringify(normalized) !== JSON.stringify(acType.value)) {
				acType.setValue(normalized, { emitEvent: false });
			}
		}
	}

	/**
	 * Minimum step for a given section.
	 */
	protected minPageForSection(section: "training" | "rating" | "signoff") {
		return section === "training" ? 1 : section === "rating" ? 2 : 3;
	}

	/**
	 * Page index to persist for a section. Never regress previously reached steps.
	 */
	protected pageFor(section: "training" | "rating" | "signoff") {
		return Math.max(this.continuePage, this.minPageForSection(section));
	}

	/**
	 * Template submit handler for Step 2/3 forms.
	 */
	onRatingFormSubmit(): void {
		console.error("submit");
	}

	protected confirmSendToCandidateAction(): Promise<boolean> {
		return confirmSendToCandidate(this.dialog);
	}

	protected confirmCompleteRatingAction(): Promise<boolean> {
		return confirmCompleteAssessment(this.dialog);
	}

	async confirmAndComplete(stepper: MatStepper): Promise<void> {
		if (!(await this.confirmCompleteRatingAction())) return;

		const complete = (
			this as TsRatingFormV2Base & { onStepperComplete?: (stepper: MatStepper) => Promise<void> | void }
		).onStepperComplete;
		if (complete) {
			await complete.call(this, stepper);
		}
	}

	/**
	 * Template handler to move the stepper back one step
	 * AND persist the previous continuePage to the draft.
	 */
	// TsRatingFormV2Base
	onStepBack(stepper: MatStepper): void {
		const prevIndex = Math.max(0, stepper.selectedIndex - 1);
		const prevPage = prevIndex + 1; // step -> 1..3

		// prevent valueChanges autosaves from immediately re-raising the page
		this.containerRatingsService.silenceAutosave = true;

		// keep local state consistent
		this.continuePage = prevPage;

		this.containerRatingsService
			.upsertDraft(this.assessmentOfflineId, {}, prevPage, undefined, { forcePage: true })
			.finally(() => {
				stepper.previous();
				// re-enable autosave after the UI settles
				setTimeout(() => (this.containerRatingsService.silenceAutosave = false), 250);
			});
	}

	/**
	 * Read-only draft accessors.
	 */
	getDraft(): DraftItem | undefined {
		return this.containerRatingsService.draft;
	}
	getCandidateRatingData(): CandidateRating | undefined {
		return this.containerRatingsService.draft?.candidateRating;
	}
	getSignOffDetailsData(): SignOffDetails | undefined {
		return this.containerRatingsService.draft?.signOffDetails;
	}

	/**
	 * Success handler for sign-off submission.
	 */
	onSuccessSignOff(): void {
		this.signOffSuccess = true;

		if (this.candidateList.length == this.completedCandidateList.length) {
			localStorage.setItem(SESSION.SESSION_KEY_CANDIDATES, JSON.stringify(this.completedCandidateList));
			this.containerRatingsService.deleteSelectedDraft();
			setTimeout(() => {
				this.loadingService.setLoading(false);
				this.router.navigate([RouteType.RATING_QUICK_SUMMARY_DETAILS]);
			}, 1000);
		}
	}

	/**
	 * Error handler for sign-off submission.
	 */
	onErrorSignOff(error: any): void {
		this.loadingService.setLoading(false);
		this.signOffSuccess = false;
		console.error(error);
	}

	/**
	 * Attach autosave handlers to Step 1 and Step 2 root forms.
	 * Suppressed during bootstrap and when silenceAutosave is set.
	 */
	protected attachFormListenersOnce(): void {
		if (this.listenersAttached) return;
		this.listenersAttached = true;

		const toJson = (v: any) => JSON.stringify(v);

		this.trainingInfoForm.valueChanges
			.pipe(
				takeUntil(this.destroy$),
				debounceTime(300),
				rxFilter(
					() => !this.isBootstrapping && !this.containerRatingsService.silenceAutosave && !!this.assessmentOfflineId,
				),
				rxMap(toJson),
				distinctUntilChanged(),
			)
			.subscribe(() => this.saveTrainingDraft());

		this.ratingRootForm.valueChanges
			.pipe(
				takeUntil(this.destroy$),
				debounceTime(400),
				rxFilter(
					() => !this.isBootstrapping && !this.containerRatingsService.silenceAutosave && !!this.assessmentOfflineId,
				),
				rxMap(toJson),
				distinctUntilChanged(),
			)
			.subscribe(() => this.saveAsDraft(this.pageFor("rating")));
	}

	/**
	 * Persist Step 1 values to the current draft. If the candidate count changed,
	 * signal that Step 3 forms must be rebuilt on next entry.
	 */
	protected async saveTrainingDraft() {
		try {
			const prevCount = this.containerRatingsService.draft?.trainingInformation?.candidates?.length ?? 0;

			const trainingInformation = this.getTrainingInformation();
			const nextCount = trainingInformation?.candidates?.length ?? 0;

			if (nextCount !== prevCount) {
				this.isStep2FormCreated = false;
			}

			await this.saveToDrafts(this.pageFor("training"), { trainingInformation });
		} catch (e) {
			console.error("saveTrainingDraft failed:", e);
		}
	}

	/**
	 * Persist Step 3 values to the draft.
	 */
	protected async saveSignOffDraft() {
		try {
			const signOffDetails = this.collectSignOffDetailsFromForm();
			console.log("signOffDetails", signOffDetails);
			await this.saveToDrafts(this.pageFor("signoff"), { signOffDetails });
		} catch (e) {
			console.error("saveSignOffDraft failed:", e);
		}
	}

	/**
	 * Save the draft at a specific page index.
	 * Step 1 includes the trainingInformation payload.
	 */
	async saveAsDraft(page: number) {
		const data: Partial<DraftItem> = {};
		if (page === 1) {
			data.trainingInformation = this.getTrainingInformation();
		} else {
			if (!this.assessmentOfflineId) return;
		}
		await this.saveToDrafts(page, data);
	}

	/**
	 * Ask for user confirmation to save and then persist a draft snapshot.
	 */
	async saveAsTemp(page: number) {
		let data: Partial<DraftItem> = {};
		if (page === 1) {
			data.trainingInformation = this.getTrainingInformation();
		}
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "save-draft-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Save as Draft",
				modalContent: "Save this assessment as draft?",
				modalBtnConfirmText: "Save",
				modalBtnCancelText: "Cancel",
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result && result.close) this.saveToDrafts(page, data, true);
			sub.unsubscribe();
		});
	}

	/**
	 * Create or update the current draft:
	 * - Pass only the caller delta. The write queue overlays that on the fresh row.
	 * - Attach form details the first time only.
	 * - Update local draft id references.
	 */
	protected async saveToDrafts(page: number, data: Partial<DraftItemForm>, hasMessage: boolean = false) {
		try {
			const existing = this.assessmentOfflineId
				? await this.containerRatingsService.getDraftById(this.assessmentOfflineId)
				: undefined;

			const resolvedRaw = resolveFormDetailsForRating(this.rawFormData);
			let includeFormDetails: typeof this.rawFormData | undefined;
			if (!existing?.formDetails) {
				includeFormDetails = resolvedRaw ?? this.rawFormData;
			} else if (existing.formDetails.configVersion == null && resolvedRaw) {
				includeFormDetails = { ...existing.formDetails, configVersion: resolvedRaw.configVersion };
			}

			const payload = draftSavePayload(data ?? {});

			const id = await this.containerRatingsService.upsertDraft(
				this.assessmentOfflineId,
				payload,
				page,
				includeFormDetails,
			);

			this.assessmentOfflineId = id;
			this.draftId = id;
			this.loadingService.setLoading(false);

			if (hasMessage) this.containerRatingsService.onSaveToDraftsSuccess();
		} catch (err) {
			console.error("saveToDrafts failed:", err);
			this.assessmentOfflineId = undefined;
			this.draftId = undefined;
		}
	}

	/**
	 * Programmatically select a step even when the stepper is linear.
	 * Temporarily disables linear mode and restores it afterwards.
	 */
	protected forceSelectStep(index: number) {
		if (!this.stepper) return;
		const wasLinear = this.stepper.linear;
		this.stepper.linear = false;
		this.stepper.steps.forEach((s, i) => {
			if (i < index) (s as any).completed = true;
		});
		this.stepper.selectedIndex = index;
		setTimeout(() => (this.stepper.linear = wasLinear));
	}

	/**
	 * Smooth scroll to the top of the page container after a short delay.
	 */
	protected gotoTop() {
		setTimeout(() => {
			const element = document.getElementById("cbta-web-page-container") as HTMLElement;
			element.scrollTop = 0;
		}, 10);
	}

	/**
	 * Immediate scroll to the top of the page container.
	 */
	protected scrollToTop() {
		const element = document.getElementById("cbta-web-page-container") as HTMLElement;
		element.scrollTop = 0;
	}

	/**
	 * Load a draft by id, patch Step 1 values, and move to the last visited step.
	 */
	async loadDraftValues(draftId: string) {
		this.isBootstrapping = true;
		try {
			const draft = await this.containerRatingsService.getDraftById(draftId);
			if (!draft) return;

			this.assessmentOfflineId = draftId;
			await this.containerRatingsService.loadDraftItem(draftId);
			await this.waitForOptions();
			this.loadDraftTrainingInfo(draft);
			await validateDraftCandidateStaffNumbers(
				this.candidates,
				this.httpService,
				this.profileService.profile?.staff_number,
				this.containerRatingsService.formType,
			);
			this.trainingInfoForm.updateValueAndValidity({ emitEvent: false });
			this.rehydrateFlightSimulatorAcTypeFields();
			(this as any).cd?.detectChanges?.();
			await this.goToTrainingPage(draft.continuePage ?? 1);
		} catch (error) {
			console.error("loading draft values failed:", error);
		} finally {
			this.isBootstrapping = false;
		}
	}

	/**
	 * Move to a particular step, performing the same actions as user-driven navigation.
	 */
	protected async goToTrainingPage(page: number) {
		this.continuePage = page;

		if (page === 2) {
			await this.onStepperNext(this.stepper as any);
			return;
		}
		if (page === 3) {
			await this.onStepperNext(this.stepper as any);
			this.onStepperNextSignOff(this.stepper as any, true);
			return;
		}
	}

	/** Ensure draft assessment id exists before building sign-off subforms. */
	protected ensureAssessmentOfflineId(): void {
		if (!this.assessmentOfflineId) {
			this.assessmentOfflineId = this.draftId ?? uuid.v4();
		}
	}

	/** Override in 3-step forms (TS5/TS6) to populate `signOffForm` on step entry. */
	protected rebuildSignOffFormsIfNeeded(): void {}

	/** Rebuild sign-off forms when breadcrumb/stepper jumps skip transition handlers. */
	protected onStepperSelectionChanged(selectedIndex: number): void {
		if (selectedIndex === 2) {
			this.rebuildSignOffFormsIfNeeded();
		}
	}

	// Abstract hooks implemented by the subclass.

	protected abstract getTrainingInformation(): any;
	protected abstract collectSignOffDetailsFromForm(): any;
	abstract onStepperNext(stepper: MatStepper): Promise<void>;
	abstract onStepperNextSignOff(stepper: MatStepper, bypassValidation?: boolean): void;
	protected abstract loadDraftTrainingInfo(draft: DraftItem): void;

	// Shared conveniences for subclasses.

	protected saveFormType(value: { code: string }): void {
		this.formTypeCtrl.patchValue(value.code, { emitEvent: false });
	}

	protected saveSyllabusStartDate(value: string): void {
		this.syllabusStartDateCtrl.patchValue(value);
	}

	protected getCandidateList(): Array<string> {
		this.candidateList = this.candidates.value.map((v: any) => v.name);
		return this.candidateList;
	}

	protected canAddCandidate(): boolean {
		const currentCount = this.candidates?.length ?? 0;

		const rawFormType = this.formTypeCtrl?.value;
		const normalizedFormType =
			typeof rawFormType === "string"
				? rawFormType.toLowerCase()
				: typeof rawFormType === "object" && rawFormType
					? (rawFormType.code ?? rawFormType.value ?? rawFormType.formType ?? "").toString().toLowerCase()
					: "";

		const isTs1 = normalizedFormType === CONSTANT.FORM_TYPE.ts_1 || normalizedFormType === "ets_1";

		const maxAllowed = isTs1 ? this.MAX_CANDIDATES_TS1 : this.MAX_CANDIDATES;
		const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
		return isOnline && currentCount < maxAllowed;
	}

	protected onPrev() {
		if (this.current > 0) {
			this.prev = this.current;
			this.current--;
		}
	}

	protected onNext() {
		if (this.current < this.candidateList.length - 1) {
			this.prev = this.current;
			this.current++;
		}
	}

	protected isLeftTransition(idx: number): boolean {
		return this.current === idx ? this.prev > this.current : this.prev < this.current;
	}

	protected normalizeCandidateView() {
		const len = this.candidateList.length;
		if (len <= 0) {
			this.current = 0;
			this.prev = -1;
			return;
		}
		if (this.current >= len) {
			this.prev = this.current;
			this.current = len - 1;
		}
		if (this.current < 0) this.current = 0;
	}

	protected get trainingStartDate(): Date {
		return moment(this.syllabusStartDateCtrl.value).toDate();
	}

	protected get trainingEndDate(): Date {
		return moment(this.syllabusEndDateCtrl.value).toDate();
	}

	/**
	 * Builds preview summary data for the spider-web chart from draft state.
	 *
	 * IMPORTANT — Reset (repeat evaluation) handling:
	 * The draft stores ALL addRemoveOb events across the session, including
	 * events from before a maneuver was reset. A "reset maneuver" restores
	 * observations to predefined values, invalidating prior events. We must
	 * filter out pre-reset events using `resetManeuvers` timestamps before
	 * deduplicating, otherwise stale values (e.g. inflated activeAttempt
	 * from repeated observations) bleed into the calculation.
	 *
	 * The backend does not have this issue because `syncStagingAssessment`
	 * replays events chronologically — `resetManeuverEvaluation` deletes
	 * all observation history for the maneuver, so only post-reset events
	 * are persisted before the rating calculation runs.
	 */
	protected getPreviewSummaryData(): any[] {
		const draft = this.containerRatingsService?.draft;

		if (!draft) {
			return [];
		}

		const manuevers = draft?.formDetails.assessmentConfig;
		const rates = draft?.candidateRating?.temRate ?? {};
		const candidates = draft.trainingInformation.candidates;
		const selectedOBS = draft.candidateRating?.selectedOBs ?? [];

		const candidateAddRemoveOb =
			draft.candidateRating?.addRemoveOb?.map((d) => {
				return {
					candidateId: d.candidateId,
					observationCode: d.observationCode,
					maneuverCode: d.maneuverCode,
					isRemoved: d.isRemoved,
					isDisabled: d.isDisabled,
					isRepeat: d.isRepeat ?? false,
					isReset: d.isReset ?? false,
					createdAt: d.createdAt,
					totalAttempt: d.totalAttempt,
					activeAttempt: d.activeAttempt,
					candidateRole: d.candidateRole,
					observationCategory: d.observationCode.split("_")[0]?.toUpperCase(),
				};
			}) ?? [];

		//predefined OBs (legacy / shared). TS1-v3 rebuilds per candidate excluding N/A roles.
		const predefinedObsAll: IObservationRating[] = [];
		for (const assessment of manuevers) {
			for (const d of assessment.observations) {
				predefinedObsAll.push({
					observationCode: d.code,
					maneuverCode: assessment.maneuverType.code,
					totalAttempt: d.attempt,
					activeAttempt: d.attempt,
					isRemoved: false,
				});
			}
		}

		const isTs1V3Draft = isTs1V3OrV4RatingFormVersion(draft?.ratingFormVersion);

		// Legacy (non-v3): exclude maneuvers with TEM rate < 1 (incomplete / N/A).
		// TS1-v3: N/A PF/PM maneuver ratings must NOT exclude OBs from A/B — computed per candidate below.
		const ratesEntries = Object.entries(rates);
		const maneuversCodeToExclude: { candidateId: string; maneuverCode: string }[] = [];
		if (!isTs1V3Draft) {
			for (const [key, value] of ratesEntries) {
				if (value.rate < 1) {
					const [candidateId, assessmentId] = key.split(".");
					maneuversCodeToExclude.push({
						candidateId,
						maneuverCode:
							manuevers.find((a) => a.assessmentId === assessmentId)?.maneuverType.code ?? "",
					});
				}
			}
		}

		const candidateList = (candidates ?? []).filter(
			(c): c is typeof c & { candidateId: string } => !!c.candidateId,
		);
		const crewRoles =
			draft?.candidateRating?.crewRoleAssignment?.rolesByCandidateIndex ?? [];
		const hasSecondCandidate = candidateList.length >= 2;

		return candidateList.map(({ candidateId, name: candidateName }, candidateIndex) => {
				const isTs1V3 = isTs1V3Draft;
				const lastDisabledByObsCode = new Map<string, boolean>();
				const commentsByOb = filterSelectedOBsForSummary(selectedOBS, candidateId).reduce<any>((acc, current) => {
					const {
						ob: obRaw,
						action,
						instructorComment: comment,
						candidateRole: role,
						maneuverCode,
						remarks,
						rate,
						isDisabled,
						isReset,
						observationCode,
					} = current;

					const ob = obRaw.split(" ")[0];
					const obsCode = observationCode ?? "";
					const scopeKey = isTs1V3
						? `${maneuverCode ?? ""}::${obsCode}::${role ?? ""}`
						: `${maneuverCode ?? ""}::${obsCode}`;
					const lastActionDisabled = lastDisabledByObsCode.get(scopeKey) ?? false;

					// Include all comments including TEM
					acc[ob] = acc[ob] || [];
					acc[ob].push({
						ob: obRaw,
						comment,
						remarks,
						role,
						action,
						rate,
						isDisabled: isDisabled ?? false,
						isReset: isReset ?? false,
						lastActionDisabled,
					});

					if (obsCode) {
						lastDisabledByObsCode.set(scopeKey, !!isDisabled);
					}

					return acc;
				}, {});

				const comments: any[] = [];

				for (const key of Object.keys(commentsByOb)) {
					const values = commentsByOb[key] ?? [];

					comments.push({
						ob: key,
						description: "", // to do get description
						sections: values,
					});
				}

				// Build a map of the last reset time per maneuver for this candidate.
				// A "reset maneuver" (repeat evaluation) restores all observations on
				// that maneuver back to their predefined values. Any addRemoveOb events
				// that occurred BEFORE the last reset for a maneuver are stale and must
				// be excluded — only post-reset events reflect the current UI state.
				const resetManeuvers = draft.candidateRating?.resetManeuvers ?? [];
				const lastResetTimeMap = new Map<string, number>();
				for (const rm of resetManeuvers) {
					if (rm.candidateId !== candidateId) continue;
					const ts = new Date(rm.createdAt).getTime();
					const prev = lastResetTimeMap.get(rm.maneuverCode) ?? 0;
					if (ts > prev) lastResetTimeMap.set(rm.maneuverCode, ts);
				}

				const lastObResetTimeMap = buildLastObResetTimeMap(draft.candidateRating?.addRemoveOb ?? [], candidateId);

				//get Unique Obs by last createdAt and candidateId
				const filteredAddRemoveOb = filterAddRemoveObForRating(
					candidateAddRemoveOb,
					candidateId,
					lastObResetTimeMap,
				).filter((d) => {
					// Exclude events that occurred before the last reset for this maneuver
					const lastReset = lastResetTimeMap.get(d.maneuverCode);
					if (!lastReset) return true;
					return new Date(d.createdAt).getTime() >= lastReset;
				});

				const ratingAddRemoveOb = isTs1V3
					? applyManeuverOutcomeNaDisabledToEvents(
							filteredAddRemoveOb,
							draft.candidateRating?.maneuverOutcomes,
							candidateIndex,
							hasSecondCandidate,
						)
					: filteredAddRemoveOb;

				const uniqueCandidateAddRemoveOb = isTs1V3
					? mergeTs1V3ObEventsForRatingCalc(ratingAddRemoveOb)
					: ratingAddRemoveOb
							.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
							.filter(
								(d, index, self) =>
									index ===
									self.findIndex(
										(t) =>
											t.observationCode === d.observationCode &&
											t.maneuverCode === d.maneuverCode &&
											t.candidateId === d.candidateId,
									),
							);

				const preferredRole =
					(crewRoles[candidateIndex] as CandidateRole | undefined) ??
					(candidateIndex === 0 ? CandidateRole.PF : CandidateRole.PM);
				const excludeManeuverCodes = isTs1V3
					? buildTs1V3ManeuverCodesToExclude({
							candidateId,
							preferredRole,
							assessmentConfig: manuevers ?? [],
							maneuverOutcomes: draft.candidateRating?.maneuverOutcomes ?? [],
							candidateIndex,
							hasSecondCandidate,
							temRate: rates,
						})
					: maneuversCodeToExclude
							.filter((v) => v.candidateId === candidateId)
							.map((v) => v.maneuverCode);
				const excludeSet = [...new Set(excludeManeuverCodes)];

				const shouldExcludeDisabledOb = (d: {
					isDisabled?: boolean | null;
					activeAttempt?: number | null;
				}) =>
					isTs1V3
						? shouldExcludeDisabledObForTs1V3(d)
						: !!d.isDisabled;

				const disabledObKeys = isTs1V3
					? buildTs1V3FullyDisabledObKeys(ratingAddRemoveOb)
					: (() => {
							const keys = new Set<string>();
							for (const d of uniqueCandidateAddRemoveOb) {
								if (shouldExcludeDisabledOb(d)) {
									keys.add(`${d.maneuverCode}::${d.observationCode}`);
								}
							}
							return keys;
						})();

				const naRoleKeys = isTs1V3 ? buildTs1V3NaRoleObKeys(ratingAddRemoveOb) : new Set<string>();
				const maneuverOutcomes = draft.candidateRating?.maneuverOutcomes ?? [];
				const predefinedObs: IObservationRating[] = isTs1V3
					? (() => {
							const rows: IObservationRating[] = [];
							for (const assessment of manuevers ?? []) {
								for (const d of assessment.observations ?? []) {
									if (
										isTs1V3ConfigObsExcludedByNa({
											maneuverCode: assessment.maneuverType.code,
											observationCode: d.code,
											candidateRole: d.candidateRole,
											naRoleKeys,
										}) ||
										isTs1V3ManeuverRoleOutcomeNa({
											maneuverOutcomes,
											maneuverCode: assessment.maneuverType.code,
											candidateRole: d.candidateRole,
											candidateIndex,
											hasSecondCandidate,
										})
									) {
										continue;
									}
									rows.push({
										observationCode: d.code,
										maneuverCode: assessment.maneuverType.code,
										totalAttempt: d.attempt,
										activeAttempt: d.attempt,
										isRemoved: false,
										candidateRole: d.candidateRole ?? null,
									});
								}
							}
							return rows;
						})()
					: predefinedObsAll;

				/** Post-reset chronological history for How Often UI counter replay (not deduped per OB). */
				const orderedActiveObsChronological: IObservationRating[] = ratingAddRemoveOb
					.filter((d) => !excludeSet.includes(d.maneuverCode))
					.filter((d) => !disabledObKeys.has(`${d.maneuverCode}::${d.observationCode}`))
					// TS1-v3: drop N/A snapshots and prior events for N/A'd roles (orphans inflate How Often, e.g. 2/3→B=2).
					.filter((d) => !(isTs1V3 && !!d.isDisabled))
					.filter(
						(d) =>
							!(
								isTs1V3 &&
								(isTs1V3ConfigObsExcludedByNa({
									maneuverCode: d.maneuverCode,
									observationCode: d.observationCode,
									candidateRole: d.candidateRole,
									naRoleKeys,
								}) ||
									isTs1V3ManeuverRoleOutcomeNa({
										maneuverOutcomes,
										maneuverCode: d.maneuverCode,
										candidateRole: d.candidateRole,
										candidateIndex,
										hasSecondCandidate,
									}))
							),
					)
					.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
					.map((d) => ({
						isRemoved: d.isRemoved ?? false,
						observationCode: d.observationCode,
						maneuverCode: d.maneuverCode,
						totalAttempt: d.totalAttempt ?? 1,
						activeAttempt: d.activeAttempt ?? 1,
						candidateRole: d.candidateRole ?? null,
					}));

				//active Obs
				const activeObs: IObservationRating[] = [];
				const disabledObs: IPreviewDisabledObservation[] = [];

				for (const d of uniqueCandidateAddRemoveOb) {
					if (isTs1V3 && !includeTs1V3ObInRatingNewObs(d)) {
						continue;
					}

					activeObs.push({
						isRemoved: d.isRemoved ?? false,
						observationCode: d.observationCode,
						maneuverCode: d.maneuverCode,
						totalAttempt: d.totalAttempt ?? 1,
						activeAttempt: d.activeAttempt ?? 1,
						candidateRole: d.candidateRole ?? null,
					});
				}

				for (const key of disabledObKeys) {
					const sep = key.indexOf("::");
					if (sep < 0) continue;
					disabledObs.push({
						maneuverCode: key.slice(0, sep),
						observationCode: key.slice(sep + 2),
						maneuverId: "",
						observationId: "",
						candidateId,
					});
				}

				// Calculations
				const v4OriginalObsCodes = isTs1V4RatingFormVersion(draft?.ratingFormVersion)
					? resolveOriginalObsCodesFromAssessmentConfig(manuevers)
					: undefined;

				const howManyCalculation = this.offlineRatingService.ObservationBreakdownHowManyCalculation({
					newObs: activeObs,
					predefinedObs,
					maneuverCodeToExclude: excludeSet,
					disabledObservations: disabledObs,
					formTypeCode: this.containerRatingsService.formType,
					originalObsCodes: v4OriginalObsCodes,
				});

				const howOftenCalculation = this.offlineRatingService.ObservationBreakdownHowOftenCalculation({
					newObs: activeObs,
					predefinedObs,
					maneuverCodeToExclude: excludeSet,
					disabledObservations: disabledObs,
					formTypeCode: this.containerRatingsService.formType,
					ratingAByCategory: new Map(
						(howManyCalculation ?? []).map(({ obCategoryCode, rating }) => [obCategoryCode, rating]),
					),
					orderedNewObs: orderedActiveObsChronological,
					originalObsCodes: v4OriginalObsCodes,
				});

				// Align datasets by category order (sorted for consistent display)
				const categoryOrder = Array.from(
					new Set([
						...(howManyCalculation?.map((x) => x.obCategoryCode) ?? []),
						...(howOftenCalculation?.map((x) => x.obCategoryCode) ?? []),
					]),
				).sort((a, b) => a.localeCompare(b));

				const howManyMap = new Map((howManyCalculation ?? []).map((x) => [x.obCategoryCode, x.rating]));
				const howOftenMap = new Map((howOftenCalculation ?? []).map((x) => [x.obCategoryCode, x.rating]));

				//tem rate breakdown
				const manueverData = manuevers
					?.map((a) => {
						const { assessmentId } = a;
						const key = `${candidateId}.${assessmentId}`;
						const rate = rates[key]?.rate ?? 0;
						return {
							label: a.maneuverType.description.toUpperCase(),
							value: rate,
						};
					})
					?.filter((v) => v.value !== 0) //to exclude maneuver rated incomplete
					?.sort((a, b) => {
						// First sort by value (descending)
						if (b.value !== a.value) {
							return b.value - a.value;
						}
						// If values are equal, sort by label (ascending)
						return a.label.localeCompare(b.label);
					});

				const result = {
					id: candidateId,
					name: candidateName,
					manueverData,
					radarChartLabels: categoryOrder,
					radarChartDatasets: [
						{
							label: "How Many",
							data: categoryOrder?.map((c) => howManyMap.get(c) ?? 0),
							backgroundColor: "rgba(249, 172, 81, 0.1)",
							borderWidth: 1,
							borderColor: "#F9AC51",
							pointBackgroundColor: "#F9AC51",
							pointBorderColor: "rgba(249, 172, 81, 0.35)",
							pointBorderWidth: 8,
							pointRadius: 4,
							pointHoverRadius: 10,
						},
						{
							label: "How Often",
							data: categoryOrder?.map((c) => howOftenMap.get(c) ?? 0),
							backgroundColor: "rgba(75, 79, 166, 0.1)",
							borderWidth: 1,
							borderColor: "#4B4FA6",
							pointBackgroundColor: "#4B4FA6",
							pointBorderColor: "rgba(75, 79, 166, 0.35)",
							pointBorderWidth: 8,
							pointRadius: 4,
							pointHoverRadius: 10,
						},
					],
					comments,
				};
				return result;
			});
	}

	getSignOffValues() {
		const signoff = this.signOffForm.value as Array<{
			traineeName: string;
			trainerName: string;
			instructorLicenseNumber?: string;
			traineeSignature: File | Blob | string; // dataURL or File/Blob
			trainerSignature: File | Blob | string; // dataURL or File/Blob
			sessionCheck?: boolean;
			clearanceCheck?: boolean;
			objectiveCheck?: boolean;
		}>;

		const candidate = this.ratingForm.value as Array<{
			observations: Array<{ candidateId: string; traineeAssessmentId?: string }>;
		}>;

		return { signoff, candidate };
	}

	get hasSignatures(): boolean {
		const { signoff, candidate } = this.getSignOffValues();

		return signoff.every((s, i) => {
			const candidateId = candidate[i]?.observations?.[0]?.candidateId;
			const signature = s?.traineeSignature;

			return Boolean(candidateId && signature !== null && signature !== undefined);
		});
	}
}
